/**
 * SUBSCRIPTION CONFIGURATION
 * Single source of truth for all subscription/feature mappings
 *
 * This file defines:
 * - Page to feature mappings (required for page access)
 * - User-friendly labels for pages and features
 * - Helper functions for dynamic lookups
 */

import { PlanFeatures } from '@/lib/api/types';

/**
 * PAGE_FEATURE_MAP
 * Maps page route identifiers to the feature they require
 * Set to null if page is public (no feature required)
 *
 * Add new pages here when creating new routes
 */
export const PAGE_FEATURE_MAP: Record<string, keyof PlanFeatures | null> = {
    'dashboard': null,           // Public page - no feature required
    'search': 'emailRecords',          // Requires email search capability
    'saved': 'incidentSaving',         // Requires incident saving
    'threat-feed': 'threatFeeds',      // Requires threat feeds
    'threat-profile': 'threatProfiling', // Requires threat profiling
    'telegram': 'telegramIntelligence', // Requires Telegram Intelligence (Informant Plan only)
};

/**
 * PAGE_LABELS
 * User-friendly display names for pages
 * Used in error messages and UI elements
 */
export const PAGE_LABELS: Record<string, string> = {
    'dashboard': 'Dashboard',
    'search': 'Search',
    'saved': 'Saved Incidents',
    'threat-feed': 'Threat Feed',
    'threat-profile': 'Threat Profile',
    'telegram': 'Telegram Intelligence',
};

/**
 * FEATURE_LABELS
 * User-friendly display names for features
 * Matches PlanFeatures interface keys
 */
export const FEATURE_LABELS: Record<keyof PlanFeatures, string> = {
    corporateRecords: 'Corporate Records',
    thirdPartyRecords: 'Third Party Records',
    emailRecords: 'Email Records',
    realTimeAlerting: 'Real-Time Alerting',
    gradientTracing: 'Network Gradient Tracing', // ✅ Added user-friendly metadata mapping label
    incidentSaving: 'Incident Saving',
    threatProfiling: 'Threat Profiling',
    threatFeeds: 'Threat Feeds',
    systemInformation: 'System Information',
    ipAddresses: 'IP Addresses',
    telegramIntelligence: 'Telegram Intelligence',
};

/**
 * Get the required feature for a page
 * Returns null if page is public (no feature required)
 * Returns undefined if page doesn't exist in mapping
 */
export function getPageRequiredFeature(page: string): keyof PlanFeatures | null | undefined {
    return PAGE_FEATURE_MAP[page];
}

/**
 * Get user-friendly label for a page
 * Falls back to page ID if no label defined
 */
export function getPageLabel(page: string): string {
    return PAGE_LABELS[page] ?? page.replace('-', ' ').toUpperCase();
}

/**
 * Get user-friendly label for a feature
 * Falls back to feature name if no label defined
 */
export function getFeatureLabel(feature: keyof PlanFeatures): string {
    return FEATURE_LABELS[feature] ?? feature;
}

/**
 * Check if a page is public (no feature required)
 */
export function isPagePublic(page: string): boolean {
    return getPageRequiredFeature(page) === null;
}

/**
 * Get all available pages that require a specific feature
 */
export function getPagesForFeature(feature: keyof PlanFeatures): string[] {
    return Object.entries(PAGE_FEATURE_MAP)
        .filter(([_, requiredFeature]) => requiredFeature === feature)
        .map(([page]) => page);
}

/**
 * Get all public pages (no feature required)
 */
export function getPublicPages(): string[] {
    return Object.entries(PAGE_FEATURE_MAP)
        .filter(([_, feature]) => feature === null)
        .map(([page]) => page);
}